import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

// @mui material components
import {
  Container,
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  Modal,
  Paper,
  IconButton,
  Fade,
  Backdrop,
  Tooltip, // Tooltip eklendi
  Alert,
  CircularProgress,
} from '@mui/material';

// @mui icons
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Soru işareti ikonu
import Services from 'src/services/Services';
import {
  REQUEST_AS_ADMIN_STATUS,
  getRequestAsAdminStatus,
  refreshRequestAsAdminStatus,
} from 'src/utils/requestAsAdmin';

const formatDateTime = (timestamp) => {
  if (!timestamp || !/^\d+$/.test(String(timestamp))) return '';
  const date = new Date(Number(timestamp) * 1000);
  return (
    date.toLocaleDateString('tr-TR') +
    ' ' +
    date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  );
};

// ----------------------------------------------------------------------

// Modal stilleri
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 315,
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 2,
  fontWeight: 'bold', // Yazım hatası düzeltildi: fontweight -> fontWeight
};

export default function RequestPermission() {
  const [formData, setFormData] = useState({
    description: '',
  });

  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  const [resultDateTime, setResultDateTime] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPending = requestStatus === REQUEST_AS_ADMIN_STATUS.PENDING;
  const isRejected = requestStatus === REQUEST_AS_ADMIN_STATUS.REJECTED;

  // Login datası eski olabilir (talep app'ten yapılmış / sonuçlanmış olabilir);
  // durumu her açılışta get-profile'dan al
  useEffect(() => {
    refreshRequestAsAdminStatus()
      .then((user) => {
        setRequestStatus(getRequestAsAdminStatus(user));
        setResultDateTime(user?.requestAsAdminResultDateTime);
      })
      .finally(() => setIsStatusLoading(false));
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isStatusLoading || isPending || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await Services.SendRequestAsAdmin(formData.description);

      if (response.data && response.data.status) {
        setOpenSuccessModal(true);
        setRequestStatus(REQUEST_AS_ADMIN_STATUS.PENDING);
        // Kullanıcı tekrar login olmayacağı için localStorage'daki datayı güncelle
        await refreshRequestAsAdminStatus();
      } else {
        console.error('İşlem başarısız:', response.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setOpenSuccessModal(false);
    setFormData({ description: '' });
  };

  return (
    <>
      <Helmet>
        <title>Yetki Talep Et</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ paddingLeft: 4, marginLeft: 0 }}>
        <Typography variant="h4" sx={{ mb: 5 }}>
          Yetki Talep Et
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'transparent',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
              py: 4, // Padding artırıldı
              px: 0,
              width: '100%',
              maxWidth: '963px', // Genişlik tasarıma daha uygun olması için artırıldı
              borderRadius: '16px',
              position: 'relative',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Verilecek Yetkiler
                </Typography>
                <Tooltip
                  title="Mağaza yöneticileri, yetki talep et sayfasından depo, QR ve kullanıcı ile ilgili yetkileri takım liderinden talep edebilir."
                  arrow
                  placement="right"
                >
                  <HelpOutlineIcon
                    color="action"
                    sx={{ fontSize: 20, cursor: 'pointer' }}
                  />
                </Tooltip>
              </Stack>

              <Typography variant="body1" sx={{ color: 'text.primary', mt: 2 }}>
                App içerisinde;
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2,
                  color: 'text.secondary',
                  typography: 'body2',
                }}
              >
                <li>Depo eklenip kaldırılabilmesi</li>
                <li>Kullanıcı eklenip, aktife ve pasife alınması</li>
                <li>Statik Qr kodlarını indirip değiştirilebilmesi</li>
              </Box>

              <Typography variant="body1" sx={{ color: 'text.primary', mt: 2 }}>
                Mall Logistics Panel içerisinde;
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2,
                  color: 'text.secondary',
                  typography: 'body2',
                }}
              >
                <li>İrsaliye raporu sayfası</li>
              </Box>
            </Box>
            {/* --- ÜST BİLGİ ALANI BİTİŞ --- */}

            {isStatusLoading ? (
              <CircularProgress size={32} />
            ) : isPending ? (
              <Alert severity="info" sx={{ maxWidth: 963 }}>
                Talebiniz değerlendiriliyor. Takım liderimiz talebinizi
                sonuçlandırdıktan sonra kurumsal e-posta adresiniz üzerinden
                bilgilendirileceksiniz.
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                {isRejected && (
                  <Alert severity="error" sx={{ mb: 3, maxWidth: 963 }}>
                    {formatDateTime(resultDateTime)
                      ? `${formatDateTime(resultDateTime)} tarihinde `
                      : ''}
                    yetki talebiniz reddedildi. Dilerseniz aşağıdaki formu
                    kullanarak yeniden talepte bulunabilirsiniz.
                  </Alert>
                )}
                <Stack sx={{ gap: 3 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    name="description"
                    label="Açıklama (opsiyonel)"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Açıklama mesajınız"
                    InputLabelProps={{ shrink: true }} // Label'ın yukarıda sabit durması için
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={isSubmitting}
                    sx={{
                      backgroundColor: '#052C65', // Görseldeki koyu lacivert tonu
                      color: 'white',
                      py: 1.5,
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      maxWidth: 325,
                      '&:hover': {
                        backgroundColor: '#101B39',
                      },
                    }}
                  >
                    Formu Gönder
                  </Button>
                </Stack>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      {/* Başarı Modalı */}
      <Modal
        open={openSuccessModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openSuccessModal}>
          <Box sx={style}>
            <IconButton
              aria-label="kapat"
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>

            <CheckCircleIcon sx={{ fontSize: 60, color: '#209F63' }} />
            <Typography variant="body1">
              Talebiniz, takım liderlerimize iletilmiştir. Takım liderimiz
              talebinizi onayladıktan sonra kurumsal e-posta adresiniz üzerinden
              bilgilendirileceksiniz.
            </Typography>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}
